import { getBrowserVisibilityProp, getIsDocumentHidden } from "components/utils/app";
import { logError, sendCustomReactError } from "components/utils/logger";
import { queryDictToString } from "components/utils/query";
import { useMember } from "hooks/member";
import queryString from "query-string";
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getCountries, getNames } from "services/app-service";
import { favoriteCard } from "services/card-service";
import { bdayFetch } from "services/utils";

const getBreakpoint = (width) => {
  if (width <= 320) {
    return "XXXS";
  } else if (width > 320 && width <= 415) {
    return "XXS";
  } else if (width > 415 && width <= 767) {
    return "XS";
  } else if (width > 767 && width <= 991) {
    return "SM";
  } else if (width > 991 && width <= 1199) {
    return "MD";
  } else if (width > 1199 && width <= 1280) {
    return "LG";
  } else if (width > 1280) {
    return "XL";
  }
};

export const useOutsideClick = (callback) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [ref]);

  return ref;
};

export const useFetch = (url, fetchOptions = {}, initialData = null) => {
  let controller;
  const [state, setState] = useState({ data: {}, loading: !!url, errors: [] });

  useEffect(() => {
    controller?.abort();
    if (url) {
      fetchData();
    } else {
      setState({ data: {}, loading: false, errors: [] });
    }
  }, [url]);

  const fetchData = async () => {
    if (initialData) {
      setState({ data: initialData, loading: false, errors: [] });
    } else {
      controller = new AbortController();
      setState({ data: {}, loading: true, errors: [] });
      const { errors, ...response } = await bdayFetch(url, { ...fetchOptions, signal: controller.signal });
      setState((previousState) => ({ ...previousState, errors: errors || [], data: response || {}, loading: false }));
    }
  };

  const setData = (newData) => {
    setState({ ...state, data: newData });
  };

  return { ...state.data, loading: state.loading, errors: state.errors, fetchData, setData };
};

export const useFetchPaginated = (url, limit = 20, initialData = {}) => {
  let controller;
  const [state, setState] = useState({
    results: [],
    lastResults: [],
    loading: !!url,
    total: 0,
    offset: 0,
    hasNextPage: true,
    errors: [],
  });

  useEffect(() => {
    if (url) {
      controller?.abort();
      fetchPaginated(0);
    }
  }, [url]);

  const fetchPaginated = async (newOffset) => {
    if (initialData?.results?.constructor === Array && initialData.results.length > 0 && newOffset === 0) {
      setState((previousState) => ({
        ...previousState,
        loading: false,
        results: initialData.results,
        lastResults: initialData.results,
        total: initialData.total ?? 0,
        hasNextPage: initialData.results.length < initialData.total,
      }));
    } else {
      controller = new AbortController();
      setState((previousState) => ({
        ...previousState,
        loading: true,
        hasNextPage: true,
        results: newOffset === 0 ? [] : previousState.results,
        lastResults: newOffset === 0 ? [] : previousState.lastResults,
      }));
      const q = url?.indexOf("?") >= 0 ? "&" : "?";
      const requestUrl = `${url}${q}limit=${limit}&offset=${newOffset}`;
      const { errors, ...response } = await bdayFetch(requestUrl, { signal: controller.signal });
      const responseErrors = errors || [];
      let responseResults = [];
      if (responseErrors.length === 0) {
        if (response.results?.constructor === Array) {
          responseResults = response.results;
        } else {
          responseErrors.push({ errorMessage: "There was a problem retrieving your data" });
          await sendCustomReactError(
            `Error in API call: ${requestUrl}. Expected to return an object with results param of type array and returned ${typeof response.results}`
          );
        }
      }
      setState((previousState) => ({
        ...previousState,
        loading: false,
        results: newOffset === 0 ? responseResults : [...previousState.results, ...responseResults],
        lastResults: responseResults,
        total: response.total,
        offset: newOffset,
        hasNextPage: (newOffset === 0 ? responseResults : [...previousState.results, ...responseResults]).length < response.total,
        errors: responseErrors,
      }));
    }
  };

  const fetchNextResults = async () => {
    await fetchPaginated(state.offset + limit);
  };

  const refetchResults = async () => {
    await fetchPaginated(0);
  };

  return { ...state, fetchNextResults, refetchResults };
};

export const useWindowScrollPositions = () => {
  const [scrollPosition, setPosition] = useState({ scrollX: 0, scrollY: 0 });
  useEffect(() => {
    function updatePosition() {
      setPosition({ scrollX: window.scrollX, scrollY: window.scrollY });
    }

    window.addEventListener("scroll", updatePosition);
    updatePosition();

    return () => window.removeEventListener("scroll", updatePosition);
  }, []);
  return scrollPosition;
};

export const useWindowSize = (delay = 150) => {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const timeout = useRef();

  useLayoutEffect(() => {
    function handleResize() {
      if (timeout.current) clearTimeout(timeout.current);
      // start timing for event "completion"
      timeout.current = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, delay);
    }
    window.addEventListener("resize", handleResize);
    //handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

export const useClientRect = (dependencyArray = []) => {
  const [rect, setRect] = useState({ width: 0, height: 0 });
  const size = useWindowSize();

  const ref = useCallback(
    (node) => {
      if (node !== null) {
        setRect(node.getBoundingClientRect());
      }
    },
    [size, ...dependencyArray]
  );

  return { rect, ref };
};

export const useUserActivity = (delay = 150) => {
  const [lastUserActivity, setLastUserActivity] = useState();
  const timeout = useRef();

  useLayoutEffect(() => {
    function handleEvent() {
      if (timeout.current) clearTimeout(timeout.current);
      // start timing for event "completion"
      timeout.current = setTimeout(() => {
        setLastUserActivity(new Date());
      }, delay);
    }
    window.addEventListener("resize", handleEvent);
    window.addEventListener("scroll", handleEvent);
    window.addEventListener("mousedown", handleEvent);
    window.addEventListener("mousemove", handleEvent);
    window.addEventListener("keydown", handleEvent);
    window.addEventListener("click", handleEvent);
    window.addEventListener("touchstart", handleEvent);
    return () => {
      window.removeEventListener("resize", handleEvent);
      window.removeEventListener("scroll", handleEvent);
      window.removeEventListener("mousedown", handleEvent);
      window.removeEventListener("mousemove", handleEvent);
      window.removeEventListener("keydown", handleEvent);
      window.removeEventListener("click", handleEvent);
      window.removeEventListener("touchstart", handleEvent);
    };
  }, []);

  return lastUserActivity;
};

export const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState(() => getBreakpoint(window.innerWidth));
  const size = useWindowSize();

  useEffect(() => {
    const newBreakpoint = getBreakpoint(window.innerWidth);
    setBreakpoint(newBreakpoint);
  }, [size]);

  return breakpoint;
};

export const useIsMobile = (breakpoints = ["XXXS", "XXS", "XS"]) => {
  const [breakpoint, setBreakpoint] = useState(() => getBreakpoint(window.innerWidth));
  const size = useWindowSize();

  useEffect(() => {
    const newBreakpoint = getBreakpoint(window.innerWidth);
    setBreakpoint(newBreakpoint);
  }, [size]);

  return breakpoints.includes(breakpoint);
};

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export const useFavorites = () => {
  const { queryDict } = useQueryDict();
  const { favoriteCards, isLoggedIn, isSoftLoggedIn, updateMember } = useMember();

  const setFavorite = async (cardId) => {
    if (cardId && (isLoggedIn || isSoftLoggedIn)) {
      const isToggled = favoriteCards.includes(cardId);
      const payload = {
        cardId,
        rank: 1,
        delete: isToggled ? "1" : "0",
      };
      if (isSoftLoggedIn && queryDict.t && queryDict.e) {
        payload.t = queryDict.t;
        payload.e = queryDict.e;
      }
      const { errors } = await favoriteCard(payload);
      if (errors?.length > 0) {
        logError("Error toggling favorite.", errors);
      } else {
        let newFavoriteCards = [];
        if (isToggled) {
          newFavoriteCards = favoriteCards.filter((f) => f !== cardId);
        } else {
          newFavoriteCards = [...favoriteCards, cardId];
        }
        await updateMember({ favoriteCards: newFavoriteCards });
      }
    }
  };
  return { favorites: favoriteCards, setFavorite };
};

export const useCountries = () => {
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    getCountriesData();
  }, []);

  const getCountriesData = async () => {
    const { errors, ...result } = await getCountries();
    if (errors?.length > 0) {
      logError("Error retrieving countries.", errors);
    } else {
      setCountries(result.countries);
    }
  };

  return { countries };
};

export const useNames = () => {
  const initialArray = new Array(100).fill(null).map((v, i) => ({ position: i + 1, name: "" }));
  const [names, setNames] = useState({ topFemaleNames: initialArray, topMaleNames: initialArray });

  useEffect(() => {
    getNamesData();
  }, []);

  const getNamesData = async () => {
    const { errors, topFemaleNames, topMaleNames } = await getNames();
    if (errors?.length > 0) {
      logError("Error retrieving countries.", errors);
    } else {
      setNames({ topFemaleNames, topMaleNames });
    }
  };

  return names;
};

export const usePageVisibility = () => {
  const [isVisible, setIsVisible] = useState(getIsDocumentHidden());
  const onVisibilityChange = () => setIsVisible(getIsDocumentHidden());

  useEffect(() => {
    const visibilityChange = getBrowserVisibilityProp();
    document.addEventListener(visibilityChange, onVisibilityChange, false);
    window.addEventListener("beforeunload", function () {
      document.removeEventListener(visibilityChange, onVisibilityChange);
    });
    return () => {
      document.removeEventListener(visibilityChange, onVisibilityChange);
    };
  }, []);

  return isVisible;
};

export const useSessionStorage = (keyName, defaultValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      if (keyName) {
        const value = window.sessionStorage.getItem(keyName);
        if (value) {
          return JSON.parse(value);
        } else {
          window.sessionStorage.setItem(keyName, JSON.stringify(defaultValue));
          return defaultValue;
        }
      }
      return defaultValue;
    } catch (err) {
      return defaultValue;
    }
  });

  const setValue = (newValue) => {
    if (keyName) {
      try {
        window.sessionStorage.setItem(keyName, JSON.stringify(newValue));
      } catch (err) {
        // ignore write errors
      }
    }
    setStoredValue(newValue);
  };

  return [storedValue, setValue];
};

// Custom hook to track the scroll position of the window
export const useScrollPosition = () => {
  // State to store the current scroll position
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    // Function to update the scroll position state
    const updatePosition = () => setScrollPosition(window.scrollY);

    // Adding the event listener to track scroll events
    window.addEventListener("scroll", updatePosition);

    // Cleanup function to remove the event listener when the component unmounts
    return () => window.removeEventListener("scroll", updatePosition);
  }, []); // Empty dependency array ensures this effect runs only once on mount

  // Return the current scroll position
  return scrollPosition;
};

const QueryDictContext = createContext(null);

export const QueryDictProvider = ({ children }) => {
  const location = useLocation();
  const [landingLocation] = useState(() => location);
  const { search, pathname, key } = location;
  const isLandingPage = landingLocation.key === key;

  const queryDict = Object.entries(queryString.parse(search)).reduce((a, [k, v]) => (v == null || v == "null" ? a : ((a[k] = v), a)), {});

  const updateSearch = (newParams) => {
    const newQueryDict = { ...queryDict };
    if (newParams) {
      for (const [key, value] of Object.entries(newParams)) {
        if (value || value === 0) {
          if (`${value}` !== `${queryDict[key]}`) {
            newQueryDict[key] = value;
          }
        } else if (queryDict[key]) {
          delete newQueryDict[key];
        }
      }
    }
    return queryDictToString(newQueryDict);
  };

  const filteredSearch = (...params) => {
    let result = "";
    if (params?.length > 0) {
      params.forEach((p) => {
        if (queryDict[p]) {
          if (!result) {
            result = `?${p}=${queryDict[p]}`;
          } else {
            result = `${result}&${p}=${queryDict[p]}`;
          }
        }
      });
    }
    return result;
  };

  const showSimpleHeader =
    [
      "/birthday/group",
      "/card/create",
      "/login",
      "/register",
      "/forgot",
      "/unsubscribe",
      "/happy_birthday",
      "/name",
      "/infographic",
      "/anniversary/gift",
      "/birthday/browse",
      "/anniversary/browse",
      "/holiday/browse",
    ].includes(pathname) ||
    pathname.indexOf("/card/read") >= 0 ||
    pathname.indexOf("/card/send/view") >= 0 ||
    pathname.indexOf("/card/share") >= 0 ||
    pathname.indexOf("/card/sign") >= 0 ||
    (pathname.indexOf("/birthday/request/") >= 0 && pathname !== "/birthday/request/success" && pathname !== "/birthday/request/howto") ||
    (pathname.indexOf("/card/send/") >= 0 && pathname !== "/card/send/success");

  const showSimpleFooter = showSimpleHeader && !["/login", "/register"].includes(pathname);

  return (
    <QueryDictContext.Provider value={{ queryDict, search, pathname, key, updateSearch, filteredSearch, isLandingPage, showSimpleHeader, showSimpleFooter }}>
      {children}
    </QueryDictContext.Provider>
  );
};

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const timeout = useRef();
  const ssrInfo = window.ssr_info ? JSON.parse(window.ssr_info) : {};
  const ssrSeo = window.ssr_seo ? JSON.parse(window.ssr_seo) : {};
  const settings = ssrInfo ? ssrInfo.settings : {};
  const userId = ssrInfo ? ssrInfo.userId : undefined;
  const [alert, setAlert] = useState({});
  const [alertOpen, setAlertOpen] = useState(false);
  const [shareLink, setShareLink] = useState({});
  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  const [cardSendControlCenter, setCardSendControlCenter] = useState({});
  const [cardSendControlCenterOpen, setCardSendControlCenterOpen] = useState(false);
  const [headerMessage, setHeaderMessage] = useState("");

  const showAlert = (payload) => {
    timeout.current && clearTimeout(timeout);
    setAlert(payload);
    setAlertOpen(true);
    if (!payload.preventAutoClose) {
      timeout.current = setTimeout(() => {
        setAlertOpen(false);
        setAlert({});
      }, payload.timeout || 5000);
    }
  };

  const closeAlert = () => {
    if (alert?.onClose) {
      alert.onClose();
    }
    setAlertOpen(false);
    setAlert({});
  };

  const showShareLink = (payload) => {
    setShareLink(payload);
    setShareLinkOpen(true);
  };

  const closeShareLink = () => {
    if (shareLink?.onClose) {
      shareLink.onClose();
    }
    setShareLinkOpen(false);
    setShareLink({});
  };

  const showCardSendControlCenter = (payload) => {
    setCardSendControlCenter(payload);
    setCardSendControlCenterOpen(true);
  };

  const closeCardSendControlCenter = () => {
    if (cardSendControlCenter?.onClose) {
      cardSendControlCenter.onClose();
    }
    setCardSendControlCenterOpen(false);
    setCardSendControlCenter({});
  };

  return (
    <AppContext.Provider
      value={{
        ...settings,
        serverVersion: ssrInfo.version,
        stripePublicKey: ssrInfo.stripePublicKey,
        seo: ssrSeo,
        userId,
        alert,
        showAlert,
        closeAlert,
        alertOpen,
        headerMessage,
        setHeaderMessage,
        shareLink,
        showShareLink,
        closeShareLink,
        shareLinkOpen,
        cardSendControlCenter,
        cardSendControlCenterOpen,
        showCardSendControlCenter,
        closeCardSendControlCenter,
        setCardSendControlCenter,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const CardNavContext = createContext({ navItems: [] });

export const CardNavProvider = ({ children }) => {
  const { navItems, loading } = useFetch("/api/1/card-nav");
  return <CardNavContext.Provider value={{ navItems, loading }}>{children}</CardNavContext.Provider>;
};

export const useApp = () => useContext(AppContext);
export const useQueryDict = () => useContext(QueryDictContext);
export const useCardNav = () => useContext(CardNavContext);
